import React, { useCallback, useEffect, useState, useRef } from "react";
import { Parse } from "parse";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useGetIdentity } from "react-admin";

Parse.initialize(
  process.env.REACT_APP_APP_ID,
  null,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

export const CLKKWidgetCard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { identity } = useGetIdentity();
  const response = location?.state?.response;
  const recipientId = location?.state?.recipientId
  const amount = location?.state?.amount
  const sessionToken = location?.state?.sessionToken
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [cardInfo, setCardInfo] = useState(null);
  const [verifyingCard, setVerifyingCard] = useState(false);
  const [pushingToCard, setPushingToCard] = useState(false);
  const [iframeVisible, setIframeVisible] = useState(true);
  
  const allowedOrigins = [
    "https://pay.clkkapi.io",
    "https://pay-dev.clkkapi.io",
  ];

  const updateTransactionStatus = async (sessionId, status) => {
    try {
      const TransactionRecords = Parse.Object.extend("TransactionRecords");
      const query = new Parse.Query(TransactionRecords);
      query.equalTo("transactionIdFromStripe", sessionId);
      const txn = await query.first({ useMasterKey: true });
      // await updatePotBalance(
      //   identity?.userParentId,
      //   txn.get("transactionAmount"),
      //   "recharge"
      // );
      if (!txn) {
        console.warn("⚠️ No transaction found for session:", sessionId);
        return;
      }

      txn.set("status", status); // 2: success, 10: fail
      await txn.save(null, { useMasterKey: true });
      console.log("✅ Transaction updated with status:", status);
    } catch (err) {
      console.error("❌ Failed to update transaction:", err);
    }
  };

  const handleMessage = useCallback(
    (event) => {
      if (!allowedOrigins.includes(event.origin)) return;

      const message =
        typeof event.data === "string" ? JSON.parse(event.data) : event.data;

      switch (message?.type) {
        case "PUSH_TO_CARD_READY":
          setLoading(false);
          break;

          case "PUSH_TO_CARD_SUCCESS":
            console.log("✅ PAYMENT SUCCESS", message);
            setIframeVisible(false); // ✅ hide iframe
            setLoading(false);
          
            verifyCard().then((isVerified) => {
              if (isVerified) {
                pushToCard().finally(() => setLoading(false));
              } else {
                setLoading(false);
                setError("Card verification failed. Cannot proceed.");
              }
            });
            break;
          

        case "PAYMENT_FAILED":
          console.error("❌ PAYMENT FAILED", message.data);
          setPaymentStatus({
            status: "failed",
            reason: message.data?.reason || "Unknown error",
          });

          updateTransactionStatus(message.data?.sessionId, 10); // Fail
          break;

        case "HEIGHT_CHANGED":
          if (iframeRef.current && message.data?.height) {
            iframeRef.current.height = message.data.height;
          }
          break;

        case "ERROR":
          console.error("📛 Checkout error:", message.data);
          setError("Checkout error occurred. Please try again.");
          break;

        default:
          console.warn("⚠️ Unknown event type received:", message?.type);
          break;
      }
    },
    [navigate]
  );

  const verifyCard = useCallback(async () => {
    if (!recipientId) {
      setError("Recipient ID missing in URL");
      return false;
    }
  
    setVerifyingCard(true);
    try {
      const result = await Parse.Cloud.run("verifyClkkCard", { recipientId });
      if (result?.configuredCard) {
        setCardInfo(result.configuredCard);
        return true;
      } else {
        setError("No configured card found. Please complete setup.");
        return false;
      }
    } catch (err) {
      setError("Failed to verify card: " + err.message);
      return false;
    } finally {
      setVerifyingCard(false);
    }
  }, [recipientId]);
  
  const pushToCard = useCallback(async () => {
    setPushingToCard(true);
    try {
      const payment = await Parse.Cloud.run("pushClkkCardPayment", {
        recipientId,
        amount: amount,
        description: response?.description || "Card Payment",
        orderId: `ORDER-${Date.now()}`,
      }, { sessionToken });
  
      setPaymentStatus({ status: "success" });
  
      if (payment?.recipient?.cardLast4) {
        setCardInfo({
          last4: payment.recipient.cardLast4,
          brand: payment.recipient.cardBrand,
        });
      }
    } catch (err) {
      setError("Payment failed: " + err.message);
      setPaymentStatus({ status: "failed", reason: err.message });
    } finally {
      setPushingToCard(false);
    }
  }, [recipientId, amount, response, sessionToken]);
  
  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  return (
    <Box className="container py-4" style={{   minHeight: "100vh",
    gap: 2,
    bgcolor: "black",
     color: "white"}} bgcolor="black" >
      <Box display="flex" justifyContent="start" mb={2} >
        {/* <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/playerDashboard")}
        >
          Back
        </Button> */}
      </Box>

      {verifyingCard && (
  <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
    <CircularProgress size={20} />
    <Typography variant="body2" ml={1}>
      Verifying card...
    </Typography>
  </Box>
)}

{pushingToCard && (
  <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
    <CircularProgress size={20} />
    <Typography variant="body2" ml={1}>
      Processing payout to your card...
    </Typography>
  </Box>
)}


      {loading && !showSuccessAnimation && (
        <Box
          display="flex"
          justifyContent={"center"}
          alignItems="center"
          mb={2}
          bgcolor="black" 
        >
          <CircularProgress size={20} />
          <Typography variant="body2" ml={1}>
            Loading Checkout...
          </Typography>
        </Box>
      )}

      {paymentStatus?.status === "success" && (
        <Box
          display="flex"
          justifyContent={"center"}
          alignItems="center"
          mb={2}
          bgcolor="black" 
        >
          <Alert severity="success" sx={{ mb: 2 }}>
            Payout initiated successfully. You can close this window.
          </Alert>
        </Box>
      )}

      {paymentStatus?.status === "failed" && (
        <Box
          display="flex"
          justifyContent={"center"}
          alignItems="center"
          mb={2}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            Payment failed !
          </Alert>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {showSuccessAnimation ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="300px"
        >
        </Box>
      ) : iframeVisible && response?.publicUrl ? (
        <iframe
          ref={iframeRef}
          id="clkk-checkout"
          src={response.publicUrl}
          title="CLKK Checkout"
          width="100%"
          height="600"
          allow="payment"
    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
  />
      ) : (
        <Typography variant="body1" color="text.secondary">
          No payment URL provided.
        </Typography>
      )}
    </Box>
  );
};
